import type { PageSetup } from 'exceljs';
import type { ReportMeta, ReportTable } from './reportTable';

const INK = 'FF101828';
const MUTED = 'FF667085';
const LINE = 'FFD0D5DD';

/** `#1565C0` → `FF1565C0` (Excel ARGB); falls back to the app blue. */
function argb(hex: string | undefined): string {
  return hex && /^#[0-9a-f]{6}$/i.test(hex) ? `FF${hex.slice(1).toUpperCase()}` : 'FF1565C0';
}

const numFmt = (decimals: number) => (decimals > 0 ? `#,##0.${'0'.repeat(decimals)}` : '#,##0');

/**
 * Builds a formatted workbook: shop and report title, period, a coloured header row that stays
 * in view and has filters, numbers as real numbers (grouped, fixed decimals), dates as dates,
 * bold totals, fitted column widths and A4 page setup (landscape for wide reports).
 */
export async function buildWorkbook(table: ReportTable, meta: ReportMeta, accent?: string) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = meta.shopName;
  wb.created = new Date();
  const cols = Math.max(1, table.columns.length);
  const ws = wb.addWorksheet(meta.title.slice(0, 31).replace(/[\\/?*[\]:]/g, ' '), {
    views: [{ state: 'frozen', ySplit: 5, showGridLines: false }],
    pageSetup: {
      // A4 (9). exceljs types it as an ambient const enum, which this build cannot reference.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
      paperSize: 9 as PageSetup['paperSize'],
      orientation: cols > 6 ? 'landscape' : 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.6, header: 0.3, footer: 0.3 },
      printTitlesRow: '5:5',
    },
    headerFooter: { oddFooter: `&L${meta.shopName} · ${meta.title}&RPage &P of &N` },
  });

  const title = (row: number, text: string, size: number, bold: boolean, color: string) => {
    ws.mergeCells(row, 1, row, cols);
    const cell = ws.getCell(row, 1);
    cell.value = text;
    cell.font = { name: 'Calibri', size, bold, color: { argb: color } };
    cell.alignment = { vertical: 'middle' };
  };
  title(1, meta.shopName, 14, true, INK);
  title(2, meta.title, 12, true, INK);
  title(
    3,
    [meta.period, `Generated ${meta.generatedAt}`].filter(Boolean).join('  ·  '),
    9,
    false,
    MUTED,
  );
  ws.getRow(1).height = 22;
  ws.getRow(2).height = 18;

  const head = ws.getRow(5);
  table.columns.forEach((c, i) => {
    const cell = head.getCell(i + 1);
    cell.value = c.header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(accent) } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: c.kind === 'text' ? 'left' : 'right',
      wrapText: true,
    };
    cell.border = { bottom: { style: 'thin', color: { argb: INK } } };
  });
  head.height = 20;

  table.rows.forEach((r, ri) => {
    const row = ws.getRow(6 + ri);
    const total = table.totalRows.has(ri);
    table.columns.forEach((c, ci) => {
      const cell = row.getCell(ci + 1);
      const v = r[ci] ?? null;
      if (c.kind === 'date' && typeof v === 'string') {
        const [y, m, d] = v.split('-').map(Number);
        cell.value = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
        cell.numFmt = 'dd mmm yyyy';
      } else {
        cell.value = v;
        if (c.kind === 'number') cell.numFmt = numFmt(c.decimals);
      }
      cell.alignment = { horizontal: c.kind === 'text' ? 'left' : 'right', vertical: 'top' };
      cell.border = total
        ? {
            top: { style: 'thin', color: { argb: INK } },
            bottom: { style: 'double', color: { argb: INK } },
          }
        : { bottom: { style: 'hair', color: { argb: LINE } } };
      if (total) cell.font = { bold: true };
    });
  });

  // Widths from the longest value (numbers as they display, with grouping).
  table.columns.forEach((c, i) => {
    const lengths = [
      c.header.length,
      ...table.rows.map((r) => {
        const v = r[i];
        if (v === null || v === undefined) return 0;
        if (typeof v === 'number') return Math.abs(v).toFixed(c.decimals).length * 1.3 + 1;
        return c.kind === 'date' ? 11 : v.length;
      }),
    ];
    ws.getColumn(i + 1).width = Math.min(50, Math.max(10, Math.ceil(Math.max(...lengths)) + 2));
  });
  if (table.rows.length > 0) {
    const last = 5 + table.rows.length - (table.totalRows.has(table.rows.length - 1) ? 1 : 0);
    ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: last, column: cols } };
  }
  return wb;
}

/** Downloads the table as `<filename>.xlsx`. */
export async function downloadXlsx(table: ReportTable, meta: ReportMeta, accent?: string) {
  const wb = await buildWorkbook(table, meta, accent);
  const buffer = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `${table.filename}.xlsx`;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return a.download;
}
